import { useEffect } from "react";
import {
  ConnectionState,
  RoomEvent,
  Track,
  type LocalTrack,
  type Room,
} from "livekit-client";

/**
 * Brings the local microphone and camera back by themselves after the device
 * interrupted them (#2223 AC8–AC11): a phone call, Siri, backgrounding, lock
 * and unlock, or a headset that was plugged in or out.
 *
 * Why this exists: livekit-client only restarts a capture track the OS *ended*
 * -- and on iOS only once the page is visible again. An interruption that
 * merely *mutes* the track (the common iOS case after a call or Siri) is left
 * alone, so the others stop hearing or seeing us until someone toggles the
 * button. A capture device that disappears is not replaced on iOS either.
 *
 * How: while the page is visible and the room is connected, check each local
 * mic/camera track once a second, and at once when the page becomes visible,
 * the room reconnects, or the device list changes. A track that ended, or
 * stayed OS-muted for longer than STUCK_MS, is restarted on the same device; a
 * mic whose device vanished is restarted on the system default.
 *
 * Recovery is invisible and never overrides the user: a track the user muted
 * or turned off (`LocalTrack.isMuted`) is never touched, so mute state
 * survives every interruption (AC10, AC11).
 */

/** How often tracks are checked while the page is visible. */
const CHECK_MS = 1000;
/** How long an OS mute may last before the track is restarted. Short iOS
 *  route changes mute for a moment and come back on their own. */
const STUCK_MS = 1000;
/** Pause after a failed restart -- the interruption (an ongoing phone call)
 *  may still hold the device. */
const RETRY_AFTER_FAILURE_MS = 2000;

const SOURCES = [Track.Source.Microphone, Track.Source.Camera] as const;

/**
 * Restarts one local capture track on the same device, or on the system
 * default with `useDefaultDevice` (drops a vanished deviceId). The single
 * restart routine: the interruption recovery below and the "not heard"
 * ladder's step 1 (#2224) both go through it. Throws when the restart fails.
 */
export async function restartCaptureTrack(track: LocalTrack, useDefaultDevice = false): Promise<void> {
  await track.restartTrack(useDefaultDevice ? {} : undefined);
}

/**
 * Restarts the local mic unless it is off or the user muted it (never
 * overrides the user). Resolves true when a restart was done.
 */
export async function restartLocalMic(room: Pick<Room, "localParticipant">): Promise<boolean> {
  const track = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track as
    | LocalTrack
    | undefined;
  if (!track || track.isMuted) return false;
  await restartCaptureTrack(track);
  return true;
}

export function useLocalMediaRecovery(room: Room): void {
  useEffect(() => {
    let busy = false;
    let disposed = false;
    let checkDevices = false;
    let failedAt = 0;
    const stuckSince = new Map<Track.Source, number>();

    const inputIds = async (): Promise<Set<string> | null> => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return new Set(devices.filter((d) => d.kind === "audioinput").map((d) => d.deviceId));
      } catch {
        return null;
      }
    };

    const check = async () => {
      if (busy || disposed) return;
      if (document.visibilityState !== "visible") return;
      if (room.state !== ConnectionState.Connected) return;
      const now = Date.now();
      if (now - failedAt < RETRY_AFTER_FAILURE_MS) return;
      busy = true;
      const deviceCheck = checkDevices;
      checkDevices = false;
      try {
        for (const source of SOURCES) {
          const track = room.localParticipant.getTrackPublication(source)?.track as
            | LocalTrack
            | undefined;
          // Off, or muted/turned off by the user: not ours to restart.
          if (!track || track.isMuted) {
            stuckSince.delete(source);
            continue;
          }
          const media = track.mediaStreamTrack;
          let restart = media.readyState === "ended";
          if (!restart && media.muted) {
            const since = stuckSince.get(source) ?? now;
            stuckSince.set(source, since);
            restart = now - since >= STUCK_MS;
          } else if (!media.muted) {
            stuckSince.delete(source);
          }
          let useDefaultDevice = false;
          if (!restart && deviceCheck && source === Track.Source.Microphone) {
            const deviceId = media.getSettings?.().deviceId;
            const ids = deviceId ? await inputIds() : null;
            useDefaultDevice = !!ids && !ids.has(deviceId!);
            restart = useDefaultDevice;
          }
          if (!restart || disposed) continue;
          stuckSince.delete(source);
          try {
            await restartCaptureTrack(track, useDefaultDevice);
          } catch {
            failedAt = Date.now();
          }
        }
      } finally {
        busy = false;
      }
    };

    const checkNow = () => void check();
    const onDevicesChanged = () => {
      checkDevices = true;
      void check();
    };
    const timer = setInterval(checkNow, CHECK_MS);
    document.addEventListener("visibilitychange", checkNow);
    window.addEventListener("pageshow", checkNow);
    room.on(RoomEvent.Reconnected, checkNow);
    room.on(RoomEvent.MediaDevicesChanged, onDevicesChanged);
    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", checkNow);
      window.removeEventListener("pageshow", checkNow);
      room.off(RoomEvent.Reconnected, checkNow);
      room.off(RoomEvent.MediaDevicesChanged, onDevicesChanged);
    };
  }, [room]);
}
